import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  Flex,
  Text,
  Heading,
  Select,
  Input,
  Button,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
  Badge,
  HStack,
  VStack,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { Calculator, RotateCcw, TrendingDown, TrendingUp, Minus, AlertTriangle, Plus, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const MARGIN_PRESETS = [2.0, 2.25, 2.375, 2.5, 2.625, 2.75, 3.0];

const SCENARIO_THEMES = [
  { key: 'A', accent: 'blue',   bg: 'blue.50',   border: 'blue.400'   },
  { key: 'B', accent: 'purple', bg: 'purple.50',  border: 'purple.400' },
  { key: 'C', accent: 'teal',   bg: 'teal.50',    border: 'teal.400'   },
];

function createScenario() {
  return {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    yieldSpread: '',
    targetMargin: '',
    isMarginOverridden: false,
    marginMode: 'percent',
    marginDollar: '',
    marginDollarDisplay: '',
    isMarginDollarFocused: false,
  };
}

function formatPreset(p) {
  return p.toFixed(p % 1 === 0 ? 2 : 3);
}

function formatCurrencyInput(val) {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function parseCurrencyInput(val) {
  return val.replace(/[^0-9.]/g, '');
}

function formatDollar(num) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

export default function CPCalc({ isOpen, onClose }) {
  const { lenders: allLenders, currentUser } = useAuth();
  const [selectedLenderId, setSelectedLenderId] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [loanAmount, setLoanAmount] = useState('');
  const [loanAmountDisplay, setLoanAmountDisplay] = useState('');
  const [isLoanFocused, setIsLoanFocused] = useState(false);
  const [scenarios, setScenarios] = useState(() => [createScenario()]);
  const defaultMarginRef = useRef('');
  const stateLabelRef = useRef('');

  const brandColor = useColorModeValue('#1F4E79', '#4A9BD9');
  const brandBg = useColorModeValue('blue.50', 'blue.900');
  const cardBg = useColorModeValue('white', 'gray.700');
  const sectionBg = useColorModeValue('gray.50', 'gray.600');

  const userBranch = currentUser?.Branch || '';
  const defaultStateForBranch =
    userBranch.toLowerCase().includes('davidson') || userBranch.toLowerCase().includes(' nc')
      ? 'NC'
      : 'FL';

  const correspondentLenders = useMemo(() => {
    if (!allLenders) return [];
    return allLenders
      .filter(
        (l) =>
          !l.inactive &&
          l.isCorrespondent === true &&
          l.jaxRate !== 'BP'
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [allLenders]);

  const selectedLender = correspondentLenders.find((l) => l.id === selectedLenderId) || null;

  const loanNum = parseFloat(loanAmount);
  const hasLoan = !isNaN(loanNum) && loanNum > 0;

  function rateForState(lender, st) {
    if (st === 'NC') {
      return lender.davidsonRate && lender.davidsonRate !== 'BP' ? lender.davidsonRate : '';
    }
    if (st === 'FL') {
      return lender.jaxRate && lender.jaxRate !== 'BP' ? lender.jaxRate : '';
    }
    const fallback =
      defaultStateForBranch === 'NC' ? lender.davidsonRate : lender.jaxRate;
    return fallback && fallback !== 'BP' ? fallback : '';
  }

  useEffect(() => {
    const lender = correspondentLenders.find((l) => l.id === selectedLenderId) || null;
    if (!lender) {
      setSelectedState('');
      setScenarios((prev) =>
        prev.map((s) => ({ ...s, targetMargin: '', isMarginOverridden: false }))
      );
      defaultMarginRef.current = '';
      stateLabelRef.current = '';
      return;
    }

    const states = lender.correspondentStates || [];
    const autoState = states.includes(defaultStateForBranch)
      ? defaultStateForBranch
      : states.length > 0
        ? states[0]
        : '';

    setSelectedState((prev) => {
      if (prev && states.includes(prev)) return prev;
      return autoState;
    });
  }, [selectedLenderId, correspondentLenders, defaultStateForBranch]);

  useEffect(() => {
    const lender = correspondentLenders.find((l) => l.id === selectedLenderId) || null;
    if (!lender || !selectedState) {
      defaultMarginRef.current = '';
      stateLabelRef.current = '';
      if (!lender) {
        setScenarios((prev) => prev.map((s) => ({ ...s, targetMargin: '' })));
      }
      return;
    }

    const rate = rateForState(lender, selectedState);
    defaultMarginRef.current = rate;
    stateLabelRef.current = selectedState;
    setScenarios((prev) =>
      prev.map((s) => ({
        ...s,
        targetMargin: rate,
        isMarginOverridden: false,
        marginMode: 'percent',
        marginDollar: '',
        marginDollarDisplay: '',
      }))
    );
  }, [selectedLenderId, selectedState, correspondentLenders, defaultStateForBranch]);

  useEffect(() => {
    setScenarios((prev) =>
      prev.map((s) => {
        if (s.marginMode !== 'dollar' || !hasLoan) return s;
        const pct = parseFloat(s.targetMargin);
        if (isNaN(pct)) return s;
        const dollar = (pct / 100) * loanNum;
        return {
          ...s,
          marginDollar: String(Math.round(dollar)),
          marginDollarDisplay: formatCurrencyInput(String(Math.round(dollar))),
        };
      })
    );
  }, [loanAmount]);

  function updateScenario(index, updates) {
    setScenarios((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...updates } : s))
    );
  }

  function addScenario() {
    if (scenarios.length >= 3) return;
    const last = scenarios[scenarios.length - 1];
    setScenarios((prev) => [
      ...prev,
      {
        ...createScenario(),
        targetMargin: last.targetMargin,
        isMarginOverridden: last.isMarginOverridden,
      },
    ]);
  }

  function removeScenario(index) {
    if (scenarios.length <= 1) return;
    setScenarios((prev) => prev.filter((_, i) => i !== index));
  }

  function handleMarginChange(index, val) {
    updateScenario(index, {
      targetMargin: val,
      isMarginOverridden: val !== defaultMarginRef.current,
    });
  }

  function handlePreset(index, preset) {
    const val = formatPreset(preset);
    const updates = {
      targetMargin: val,
      isMarginOverridden: val !== defaultMarginRef.current,
    };
    if (scenarios[index].marginMode === 'dollar' && hasLoan) {
      const dollar = (preset / 100) * loanNum;
      updates.marginDollar = String(Math.round(dollar));
      updates.marginDollarDisplay = formatCurrencyInput(String(Math.round(dollar)));
    }
    updateScenario(index, updates);
  }

  function resetMargin(index) {
    const updates = {
      targetMargin: defaultMarginRef.current,
      isMarginOverridden: false,
    };
    if (scenarios[index].marginMode === 'dollar' && hasLoan) {
      const pct = parseFloat(defaultMarginRef.current);
      if (!isNaN(pct)) {
        const dollar = (pct / 100) * loanNum;
        updates.marginDollar = String(Math.round(dollar));
        updates.marginDollarDisplay = formatCurrencyInput(String(Math.round(dollar)));
      }
    }
    updateScenario(index, updates);
  }

  function switchMarginMode(index, newMode) {
    const s = scenarios[index];
    if (newMode === s.marginMode) return;
    const updates = { marginMode: newMode };
    if (newMode === 'dollar' && hasLoan) {
      const pct = parseFloat(s.targetMargin);
      if (!isNaN(pct)) {
        const dollar = (pct / 100) * loanNum;
        updates.marginDollar = String(Math.round(dollar));
        updates.marginDollarDisplay = formatCurrencyInput(String(Math.round(dollar)));
      } else {
        updates.marginDollar = '';
        updates.marginDollarDisplay = '';
      }
    }
    if (newMode === 'percent' && hasLoan) {
      const dollar = parseFloat(s.marginDollar);
      if (!isNaN(dollar)) {
        const pct = (dollar / loanNum) * 100;
        updates.targetMargin = pct.toFixed(3);
        updates.isMarginOverridden = pct.toFixed(3) !== defaultMarginRef.current;
      }
    }
    updateScenario(index, updates);
  }

  const scenarioResults = useMemo(() => {
    return scenarios.map((s) => {
      const yieldNum = parseFloat(s.yieldSpread);
      const marginNum = parseFloat(s.targetMargin);
      const hasYield = !isNaN(yieldNum);
      const hasMargin = !isNaN(marginNum);
      const pointsToCharge = hasYield && hasMargin ? marginNum + yieldNum : null;
      const pointsDollar =
        pointsToCharge !== null && hasLoan ? (pointsToCharge / 100) * loanNum : null;
      const totalCompDollar =
        hasMargin && hasLoan ? (marginNum / 100) * loanNum : null;
      const yieldDollar =
        hasYield && hasLoan ? (yieldNum / 100) * loanNum : null;
      const maxCompExceeded =
        totalCompDollar !== null &&
        selectedLender?.maxComp != null &&
        totalCompDollar > selectedLender.maxComp;
      const lenderCreditExceedsMargin = pointsToCharge !== null && pointsToCharge < 0;
      const lenderPremium = hasYield && yieldNum > 0;
      const isComplete = hasYield && hasMargin && pointsToCharge !== null;

      return {
        yieldNum, marginNum, hasYield, hasMargin,
        pointsToCharge, pointsDollar, totalCompDollar, yieldDollar,
        maxCompExceeded, lenderCreditExceedsMargin, lenderPremium, isComplete,
      };
    });
  }, [scenarios, hasLoan, loanNum, selectedLender]);

  const multiMode = scenarios.length > 1;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="1px" pb={3}>
          <Flex align="center" gap={2.5}>
            <Flex
              w={8}
              h={8}
              borderRadius="lg"
              bg={brandBg}
              align="center"
              justify="center"
            >
              <Calculator size={16} color={brandColor} />
            </Flex>
            <Box>
              <Heading size="sm">CP Calc</Heading>
              <Text fontSize="xs" color="gray.500">
                Correspondent Pricing Calculator
              </Text>
            </Box>
          </Flex>
          <DrawerCloseButton />
        </DrawerHeader>

        <DrawerBody pt={6}>
          <VStack spacing={6} align="stretch">
            {/* Section 1: Lender & State */}
            <Box>
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={4}
              >
                Lender & State
              </Text>

              <Box mb={4}>
                <Text fontSize="sm" fontWeight="medium" mb={1.5}>
                  Lender
                </Text>
                <Select
                  value={selectedLenderId}
                  onChange={(e) => setSelectedLenderId(e.target.value)}
                  placeholder="Select a lender..."
                  size="md"
                >
                  {correspondentLenders.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </Select>
              </Box>

              {selectedLender &&
                (selectedLender.correspondentStates || []).length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      Property State
                    </Text>
                    <HStack spacing={2}>
                      {selectedLender.correspondentStates.map((st) => (
                        <Button
                          key={st}
                          size="sm"
                          fontWeight="semibold"
                          variant={selectedState === st ? 'solid' : 'outline'}
                          colorScheme={selectedState === st ? 'blue' : 'gray'}
                          onClick={() => {
                            if (st !== selectedState) setSelectedState(st);
                          }}
                        >
                          {st}
                        </Button>
                      ))}
                    </HStack>
                    {!selectedState && (
                      <Text fontSize="xs" color="gray.400" mt={1.5}>
                        Select the state where the property is located
                      </Text>
                    )}
                  </Box>
                )}
            </Box>

            <Divider />

            {/* Section 2: Pricing Inputs */}
            <Box>
              <Flex justify="space-between" align="center" mb={4}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Pricing Inputs
                </Text>
                {scenarios.length < 3 && (
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={<Plus size={14} />}
                    onClick={addScenario}
                    isDisabled={!hasLoan}
                  >
                    Add Scenario
                  </Button>
                )}
              </Flex>

              <Box mb={4}>
                <Text fontSize="sm" fontWeight="medium" mb={1.5}>
                  Loan Amount <Text as="span" color="red.500">*</Text>
                </Text>
                <Input
                  inputMode="numeric"
                  value={isLoanFocused ? loanAmount : loanAmountDisplay}
                  onFocus={() => setIsLoanFocused(true)}
                  onBlur={() => {
                    setIsLoanFocused(false);
                    if (loanAmount) {
                      setLoanAmountDisplay(formatCurrencyInput(loanAmount));
                    } else {
                      setLoanAmountDisplay('');
                    }
                  }}
                  onChange={(e) => {
                    const raw = parseCurrencyInput(e.target.value);
                    setLoanAmount(raw);
                  }}
                  placeholder="e.g. 350000"
                  size="md"
                />
              </Box>

              {scenarios.map((scenario, idx) => {
                const theme = SCENARIO_THEMES[idx];
                const r = scenarioResults[idx];
                return (
                  <Box
                    key={scenario.id}
                    mb={4}
                    borderLeftWidth={multiMode ? '4px' : '0'}
                    borderLeftColor={multiMode ? theme.border : 'transparent'}
                    bg={multiMode ? theme.bg : 'transparent'}
                    borderRadius={multiMode ? 'lg' : 'none'}
                    p={multiMode ? 4 : 0}
                    transition="all 0.2s"
                  >
                    {multiMode && (
                      <Flex justify="space-between" align="center" mb={3}>
                        <Badge colorScheme={theme.accent} fontSize="xs" px={2} borderRadius="md">
                          Scenario {theme.key}
                        </Badge>
                        <IconButton
                          icon={<X size={14} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                          aria-label="Remove scenario"
                          onClick={() => removeScenario(idx)}
                        />
                      </Flex>
                    )}

                    <Box mb={4} opacity={hasLoan ? 1 : 0.5}>
                      <Text fontSize="sm" fontWeight="medium" mb={1.5}>
                        Yield Spread on Rate (%)
                      </Text>
                      <Input
                        inputMode="decimal"
                        value={scenario.yieldSpread}
                        onChange={(e) => updateScenario(idx, { yieldSpread: e.target.value })}
                        placeholder="e.g. -0.13 or 0.73"
                        size="md"
                        isDisabled={!hasLoan}
                      />
                      {r.yieldDollar !== null && (
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={r.yieldNum < 0 ? 'green.600' : 'orange.600'}
                          mt={1}
                        >
                          {formatDollar(Math.abs(r.yieldDollar))} {r.yieldNum < 0 ? 'credit' : 'premium'}
                        </Text>
                      )}
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {!hasLoan
                          ? 'Enter a loan amount first'
                          : 'Negative = lender credit (e.g. -0.13) \u00b7 Positive = lender premium (e.g. 0.73)'}
                      </Text>
                    </Box>

                    <Box opacity={hasLoan ? 1 : 0.5}>
                      <Flex justify="space-between" align="center" mb={1.5}>
                        <HStack spacing={2}>
                          <Text fontSize="sm" fontWeight="medium">
                            Target Margin
                          </Text>
                          <HStack spacing={0} borderWidth="1px" borderRadius="md" overflow="hidden">
                            <Button
                              size="xs"
                              variant={scenario.marginMode === 'percent' ? 'solid' : 'ghost'}
                              colorScheme={scenario.marginMode === 'percent' ? 'blue' : 'gray'}
                              borderRadius="0"
                              onClick={() => switchMarginMode(idx, 'percent')}
                              isDisabled={!hasLoan}
                              minW="28px"
                            >
                              %
                            </Button>
                            <Button
                              size="xs"
                              variant={scenario.marginMode === 'dollar' ? 'solid' : 'ghost'}
                              colorScheme={scenario.marginMode === 'dollar' ? 'blue' : 'gray'}
                              borderRadius="0"
                              onClick={() => switchMarginMode(idx, 'dollar')}
                              isDisabled={!hasLoan}
                              minW="28px"
                            >
                              $
                            </Button>
                          </HStack>
                        </HStack>
                        {scenario.isMarginOverridden && defaultMarginRef.current && (
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            leftIcon={<RotateCcw size={12} />}
                            onClick={() => resetMargin(idx)}
                            isDisabled={!hasLoan}
                          >
                            Reset
                          </Button>
                        )}
                      </Flex>
                      {scenario.marginMode === 'dollar' ? (
                        <Input
                          inputMode="numeric"
                          value={scenario.isMarginDollarFocused ? scenario.marginDollar : scenario.marginDollarDisplay}
                          onFocus={() => updateScenario(idx, { isMarginDollarFocused: true })}
                          onBlur={() => {
                            const updates = { isMarginDollarFocused: false };
                            if (scenario.marginDollar) {
                              updates.marginDollarDisplay = formatCurrencyInput(scenario.marginDollar);
                            } else {
                              updates.marginDollarDisplay = '';
                            }
                            const d = parseFloat(scenario.marginDollar);
                            if (!isNaN(d) && hasLoan) {
                              const pct = (d / loanNum) * 100;
                              updates.targetMargin = pct.toFixed(3);
                              updates.isMarginOverridden = pct.toFixed(3) !== defaultMarginRef.current;
                            }
                            updateScenario(idx, updates);
                          }}
                          onChange={(e) => {
                            const raw = parseCurrencyInput(e.target.value);
                            const updates = { marginDollar: raw };
                            const d = parseFloat(raw);
                            if (!isNaN(d) && hasLoan) {
                              const pct = (d / loanNum) * 100;
                              updates.targetMargin = pct.toFixed(3);
                              updates.isMarginOverridden = pct.toFixed(3) !== defaultMarginRef.current;
                            }
                            updateScenario(idx, updates);
                          }}
                          placeholder="e.g. $8,750"
                          size="md"
                          isDisabled={!hasLoan}
                        />
                      ) : (
                        <Input
                          inputMode="decimal"
                          value={scenario.targetMargin}
                          onChange={(e) => handleMarginChange(idx, e.target.value)}
                          placeholder="e.g. 2.500"
                          size="md"
                          isDisabled={!hasLoan}
                        />
                      )}
                      {!hasLoan && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Enter a loan amount first
                        </Text>
                      )}
                      {hasLoan && selectedLender && !scenario.isMarginOverridden && defaultMarginRef.current && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Defaulted from {selectedLender.name} {stateLabelRef.current} rate
                          &mdash; override anytime
                        </Text>
                      )}
                      {hasLoan && scenario.isMarginOverridden && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Enter your desired total compensation or select a preset
                        </Text>
                      )}
                      <HStack spacing={1.5} mt={2} flexWrap="wrap">
                        {MARGIN_PRESETS.map((p) => {
                          const presetStr = formatPreset(p);
                          const presetLabel =
                            scenario.marginMode === 'dollar' && hasLoan
                              ? formatDollar((p / 100) * loanNum)
                              : presetStr;
                          const isActive =
                            scenario.marginMode === 'dollar' && hasLoan
                              ? scenario.marginDollar === String(Math.round((p / 100) * loanNum))
                              : scenario.targetMargin === presetStr;
                          return (
                            <Button
                              key={p}
                              size="xs"
                              variant={isActive ? 'solid' : 'outline'}
                              colorScheme={isActive ? 'blue' : 'gray'}
                              fontWeight="medium"
                              onClick={() => handlePreset(idx, p)}
                              isDisabled={!hasLoan}
                            >
                              {presetLabel}
                            </Button>
                          );
                        })}
                      </HStack>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Section 3: Results */}
            {scenarioResults.some((r) => r.isComplete) && (
              <>
                <Divider />
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.400"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={4}
                  >
                    Results
                  </Text>

                  {scenarios.map((scenario, idx) => {
                    const r = scenarioResults[idx];
                    if (!r.isComplete) return null;
                    const theme = SCENARIO_THEMES[idx];
                    return (
                      <Box key={scenario.id} mb={multiMode ? 6 : 0}>
                        {multiMode && (
                          <Badge colorScheme={theme.accent} fontSize="xs" px={2} borderRadius="md" mb={2}>
                            Scenario {theme.key}
                          </Badge>
                        )}

                        <Box
                          bg={multiMode ? theme.bg : brandBg}
                          borderWidth="1px"
                          borderColor={multiMode ? theme.border : 'blue.200'}
                          borderRadius="xl"
                          p={5}
                          mb={4}
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            color={multiMode ? `${theme.accent}.600` : 'blue.600'}
                            textTransform="uppercase"
                            letterSpacing="wide"
                            mb={1}
                          >
                            Charge in Box A (Premium / Discount Points)
                          </Text>
                          <Text
                            fontSize="4xl"
                            fontWeight="bold"
                            color={multiMode ? `${theme.accent}.700` : brandColor}
                            lineHeight="1"
                          >
                            {r.pointsToCharge.toFixed(3)}%
                          </Text>
                          {r.pointsDollar !== null && (
                            <Text
                              fontSize="lg"
                              fontWeight="semibold"
                              color={multiMode ? `${theme.accent}.500` : 'blue.500'}
                              mt={0.5}
                            >
                              {formatDollar(Math.abs(r.pointsDollar))}
                              {r.pointsToCharge < 0 ? ' credit to borrower' : ''}
                            </Text>
                          )}
                        </Box>

                        <Box
                          bg={cardBg}
                          borderWidth="1px"
                          borderRadius="xl"
                          overflow="hidden"
                          mb={4}
                        >
                          <Flex align="center" justify="space-between" px={4} py={3}>
                            <HStack spacing={2}>
                              {r.yieldNum < 0 ? (
                                <Box color="green.500"><TrendingDown size={16} /></Box>
                              ) : r.yieldNum > 0 ? (
                                <Box color="orange.500"><TrendingUp size={16} /></Box>
                              ) : (
                                <Box color="gray.400"><Minus size={16} /></Box>
                              )}
                              <Text fontSize="sm" color="gray.600">
                                Yield Spread{' '}
                                {r.yieldNum < 0
                                  ? '(Lender Credit)'
                                  : r.yieldNum > 0
                                    ? '(Lender Premium)'
                                    : '(Par)'}
                              </Text>
                            </HStack>
                            <Text
                              fontSize="sm"
                              fontWeight="semibold"
                              color={r.yieldNum <= 0 ? 'green.600' : 'orange.600'}
                            >
                              {r.yieldNum >= 0 ? '+' : ''}
                              {r.yieldNum.toFixed(3)}%
                            </Text>
                          </Flex>

                          <Divider />

                          <Flex align="center" justify="space-between" px={4} py={3}>
                            <Text fontSize="sm" color="gray.600">
                              + Borrower-Paid Points
                            </Text>
                            <Text fontSize="sm" fontWeight="semibold">
                              {r.pointsToCharge.toFixed(3)}%
                            </Text>
                          </Flex>

                          <Divider />

                          <Flex
                            align="center"
                            justify="space-between"
                            px={4}
                            py={3}
                            bg={sectionBg}
                          >
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                              = Total Comp
                            </Text>
                            <HStack>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={multiMode ? `${theme.accent}.700` : brandColor}
                              >
                                {r.marginNum.toFixed(3)}%
                              </Text>
                              {r.totalCompDollar !== null && (
                                <Text fontSize="xs" color="gray.500">
                                  ({formatDollar(r.totalCompDollar)})
                                </Text>
                              )}
                            </HStack>
                          </Flex>
                        </Box>

                        {r.maxCompExceeded && selectedLender && (
                          <Alert status="error" borderRadius="lg" mb={3}>
                            <AlertIcon as={AlertTriangle} boxSize={4} />
                            <AlertDescription fontSize="sm">
                              Total comp of {formatDollar(r.totalCompDollar)} exceeds{' '}
                              {selectedLender.name}&apos;s max comp cap of{' '}
                              {formatDollar(selectedLender.maxComp)}
                            </AlertDescription>
                          </Alert>
                        )}

                        {r.lenderCreditExceedsMargin && (
                          <Alert status="warning" borderRadius="lg" mb={3}>
                            <AlertIcon as={AlertTriangle} boxSize={4} />
                            <AlertDescription fontSize="sm">
                              The lender credit exceeds your target margin. The borrower would
                              receive a credit of {Math.abs(r.pointsToCharge).toFixed(3)}%
                            </AlertDescription>
                          </Alert>
                        )}

                        {r.lenderPremium && (
                          <Alert status="error" borderRadius="lg" mb={3}>
                            <AlertIcon as={AlertTriangle} boxSize={4} />
                            <AlertDescription fontSize="sm">
                              The lender is charging a {r.yieldNum.toFixed(3)}% premium &mdash;
                              your full margin plus the premium must come from borrower-paid
                              points
                            </AlertDescription>
                          </Alert>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
